import React, { Suspense, useEffect } from 'react';
import { createPortal } from 'react-dom';
import AppLoader from '../AppLoader/AppLoader';
import useMapAreaPortalTarget from '../../hooks/useMapAreaPortalTarget';
import './CramMapModal.css';

/**
 * CramMapModal
 *
 * Viewport-contained modal for CRAM drills. Dashboard stays mounted behind;
 * the modal covers the map hero area with a dimmed backdrop, a bordered
 * panel, and a single close affordance. Hosts either CramSession or
 * ConceptBlockDrill, both of which manage their own interior chrome — this
 * wrapper intentionally contributes no header or footer.
 *
 * Sibling modals: DiamondMapModal (purple), BossMapModal (crimson),
 * MapCombatPanel (teal). CramMapModal uses an indigo accent.
 */
export default function CramMapModal({ node, onClose, children }) {
  useEffect(() => {
    if (!node || typeof onClose !== 'function') return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [node, onClose]);

  // Stabilized portal host — see useMapAreaPortalTarget. Mounts into
  // .deck-map-area when present, falls back to <body> during dashboard boot,
  // and never re-parents on a stray absent frame (kills the re-pop flash).
  const target = useMapAreaPortalTarget(!!node);

  if (!node) return null;
  if (!target) return null;

  const label = node.label || node.short_label || 'CRAM session';

  const stopBubble = (event) => event.stopPropagation();

  return createPortal(
    <div className="crmm-viewport">
      <div
        className="crmm-overlay"
        role="dialog"
        aria-modal="true"
        aria-label={`CRAM session: ${label}`}
        onClick={onClose}
      >
        <div className="crmm-panel" onClick={stopBubble}>
          <button
            type="button"
            className="crmm-close"
            aria-label="Close CRAM session"
            onClick={onClose}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
              />
            </svg>
          </button>

          <div className="crmm-body">
            <Suspense fallback={
              <div className="app-loader-shell app-loader-shell--panel">
                <AppLoader label="Loading CRAM session..." size="md" />
              </div>
            }>
              {children}
            </Suspense>
          </div>
        </div>
      </div>
    </div>,
    target,
  );
}
